import { useMemo, useState } from "react";
import api, { fmtINR, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Download, FileSpreadsheet, Loader2, Search, Mail } from "lucide-react";

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

function firstOfMonthISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}

export default function SalesReport() {
  const [dateFrom, setDateFrom] = useState(firstOfMonthISO);
  const [dateTo, setDateTo] = useState(todayISO);
  const [rows, setRows] = useState([]);
  const [totalSales, setTotalSales] = useState(0);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const summary = useMemo(
    () => ({
      count: rows.length,
      total: totalSales,
    }),
    [rows, totalSales]
  );

  const load = async (e) => {
    e?.preventDefault?.();
    if (!dateFrom || !dateTo) return toast.error("Select both dates");
    if (dateFrom > dateTo) return toast.error("'From' must be on or before 'To'");
    setLoading(true);
    try {
      const { data } = await api.get("/api/reports/sales", {
        params: { from: dateFrom, to: dateTo },
      });
      setRows(data.rows || []);
      setTotalSales(data.total_sales || 0);
      setLoaded(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const downloadExcel = async () => {
    if (!dateFrom || !dateTo) return toast.error("Select both dates");
    if (dateFrom > dateTo) return toast.error("'From' must be on or before 'To'");
    setDownloading(true);
    try {
      const res = await api.get("/api/reports/sales/excel", {
        params: { from: dateFrom, to: dateTo },
        responseType: "blob",
      });
      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `sales-report-${dateFrom}-to-${dateTo}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Excel downloaded");
    } catch (err) {
      let msg = err.message;
      if (err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const parsed = JSON.parse(text);
          msg = formatApiError(parsed.detail) || msg;
        } catch {
          /* keep msg */
        }
      } else {
        msg = formatApiError(err.response?.data?.detail) || msg;
      }
      toast.error(msg);
    } finally {
      setDownloading(false);
    }
  };

  const sendEmail = async () => {
    if (!dateFrom || !dateTo) return toast.error("Select both dates");
    if (dateFrom > dateTo) return toast.error("'From' must be on or before 'To'");
    setSendingEmail(true);
    try {
      const { data } = await api.post("/api/reports/sales/email", null, {
        params: { from: dateFrom, to: dateTo },
      });
      toast.success(
        `Emailed · ${data.count || 0} orders · ${fmtINR(data.total_sales || 0)}`
      );
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSendingEmail(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-12">
      <div className="mb-8">
        <div className="font-mono text-xs tracking-[0.3em] text-[color:var(--mecha-red)]">
          // SALES
        </div>
        <h1 className="font-display text-3xl sm:text-5xl mt-3 leading-none">REPORTS</h1>
      </div>

      <form
        onSubmit={load}
        className="card-brutal p-4 sm:p-5 mb-6 grid grid-cols-1 sm:grid-cols-2 gap-4 items-end"
        data-testid="sales-report-form"
      >
        <div>
          <label className="block font-mono text-[10px] tracking-[0.3em] text-zinc-500 mb-1">
            FROM
          </label>
          <input
            data-testid="sales-from"
            type="date"
            required
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="w-full h-12 bg-zinc-950 border border-zinc-800 px-3 text-white text-sm focus:outline-none focus:border-[color:var(--mecha-red)]"
          />
        </div>
        <div>
          <label className="block font-mono text-[10px] tracking-[0.3em] text-zinc-500 mb-1">
            TO
          </label>
          <input
            data-testid="sales-to"
            type="date"
            required
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="w-full h-12 bg-zinc-950 border border-zinc-800 px-3 text-white text-sm focus:outline-none focus:border-[color:var(--mecha-red)]"
          />
        </div>
        <button
          data-testid="sales-run"
          type="submit"
          disabled={loading}
          className="btn-brutal h-12 bg-[color:var(--mecha-red)] text-white text-xs font-mono tracking-widest uppercase flex items-center justify-center gap-2 disabled:opacity-60 sm:col-span-2 lg:col-span-1"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
          Run report
        </button>
        <button
          data-testid="sales-excel"
          type="button"
          onClick={downloadExcel}
          disabled={downloading}
          className="btn-brutal h-12 border border-white hover:bg-white hover:text-black text-xs font-mono tracking-widest uppercase flex items-center justify-center gap-2 disabled:opacity-60"
        >
          {downloading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Download className="h-4 w-4" />
          )}
          Download Excel
        </button>
        <button
          data-testid="sales-email"
          type="button"
          onClick={sendEmail}
          disabled={sendingEmail}
          className="btn-brutal h-12 border border-zinc-700 hover:border-white text-xs font-mono tracking-widest uppercase flex items-center justify-center gap-2 disabled:opacity-60"
        >
          {sendingEmail ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Mail className="h-4 w-4" />
          )}
          Send Email
        </button>
      </form>
      <div className="mb-6 text-xs text-zinc-500 leading-relaxed max-w-3xl">
        Monthly auto-email runs at 23:59 IST on the last day of each month via Brevo (set
        BREVO_API_KEY). Use Send Email to test the same Excel delivery for the selected range.
      </div>

      {loaded && (
        <div className="grid grid-cols-2 gap-4 mb-6 max-w-xl">
          <div className="card-brutal p-4">
            <div className="font-mono text-[10px] tracking-[0.3em] text-zinc-500">
              ORDERS
            </div>
            <div className="font-display text-3xl mt-2" data-testid="sales-count">
              {summary.count}
            </div>
          </div>
          <div className="card-brutal p-4">
            <div className="font-mono text-[10px] tracking-[0.3em] text-zinc-500">
              TOTAL SALES
            </div>
            <div className="font-display text-3xl mt-2" data-testid="sales-total">
              {fmtINR(summary.total)}
            </div>
          </div>
        </div>
      )}

      <div className="card-brutal overflow-x-auto">
        {!loaded ? (
          <div className="p-12 text-center text-zinc-500 text-sm flex flex-col items-center gap-3">
            <FileSpreadsheet className="h-8 w-8 text-zinc-600" />
            Pick a date range and run the report.
          </div>
        ) : rows.length === 0 ? (
          <div className="p-12 text-center text-zinc-500 text-sm">
            No sales in this date range.
          </div>
        ) : (
          <table className="w-full min-w-[720px]" data-testid="sales-table">
            <thead>
              <tr className="border-b border-zinc-800">
                {[
                  "CUSTOMER NAME",
                  "DATE OF PURCHASE",
                  "TOTAL AMOUNT",
                  "STATE",
                  "AWB",
                ].map((h) => (
                  <th
                    key={h}
                    className="text-left font-mono text-[10px] tracking-[0.3em] text-zinc-500 p-4"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr
                  key={`${r.order_number}-${i}`}
                  className="border-b border-zinc-900 hover:bg-zinc-950"
                >
                  <td className="p-4 text-sm">{r.customer_name || "—"}</td>
                  <td className="p-4 font-mono text-sm text-zinc-300">
                    {r.date_of_purchase || "—"}
                  </td>
                  <td className="p-4 font-mono text-sm">{fmtINR(r.total_amount)}</td>
                  <td className="p-4 text-sm text-zinc-300">{r.state || "—"}</td>
                  <td className="p-4 font-mono text-sm text-zinc-300">
                    {r.awb || "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
